import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Upload, Download, Trash2, FileText, Search, FolderPlus, Folder, FolderOpen,
  ChevronRight, ChevronDown, Shield, X, MoreVertical, Pencil, HardDrive, Image, Film, File
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

function getFileIcon(contentType) {
  if (contentType?.startsWith('image/')) return Image;
  if (contentType?.startsWith('video/')) return Film;
  return FileText;
}

export default function Dokumente() {
  const { API, user: currentUser } = useAuth();
  const [folders, setFolders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [users, setUsers] = useState([]);
  const [storage, setStorage] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [search, setSearch] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [showPerms, setShowPerms] = useState(false);
  const [permsTarget, setPermsTarget] = useState(null);
  const [permsType, setPermsType] = useState('folder');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParent, setNewFolderParent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const canManage = currentUser?.role === 'admin' || currentUser?.role === 'projektleiter';

  const load = useCallback(async () => {
    const [f, d, u, s] = await Promise.all([
      axios.get(`${API}/folders`), axios.get(`${API}/documents`),
      axios.get(`${API}/users`), axios.get(`${API}/storage/stats`)
    ]);
    setFolders(f.data); setDocs(d.data); setUsers(u.data); setStorage(s.data);
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const rootFolders = folders.filter(f => !f.parent_id);
  const getChildren = (parentId) => folders.filter(f => f.parent_id === parentId);
  const getFolderDocs = (folderId) => docs.filter(d => d.folder_id === folderId);
  const getUnsortedDocs = () => docs.filter(d => !d.folder_id);
  const toggleExpand = (id) => setExpandedFolders(prev => ({ ...prev, [id]: !prev[id] }));

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const res = await axios.post(`${API}/folders`, { name: newFolderName, parent_id: newFolderParent });
      setFolders(prev => [...prev, res.data]);
      if (newFolderParent) setExpandedFolders(prev => ({ ...prev, [newFolderParent]: true }));
      setShowNewFolder(false); setNewFolderName(''); setNewFolderParent(null);
      toast.success('Ordner erstellt');
    } catch { toast.error('Fehler'); }
  };

  const handleDeleteFolder = async (id) => {
    try {
      await axios.delete(`${API}/folders/${id}`);
      await load();
      if (selectedFolder === id) setSelectedFolder(null);
      toast.success('Ordner und Inhalte gelöscht');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const openRename = (folder) => {
    setRenameTarget(folder);
    setRenameName(folder.name);
    setShowRename(true);
  };

  const handleRename = async () => {
    if (!renameName.trim()) return;
    try {
      const res = await axios.put(`${API}/folders/${renameTarget.id}`, { name: renameName });
      setFolders(prev => prev.map(f => f.id === renameTarget.id ? res.data : f));
      setShowRename(false); setRenameTarget(null);
      toast.success('Ordner umbenannt');
    } catch { toast.error('Fehler'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 300 * 1024 * 1024) {
      toast.error('Datei zu groß. Maximum: 300 MB');
      e.target.value = ''; return;
    }
    if (storage && storage.used_bytes + file.size > storage.max_bytes) {
      toast.error('Speicherlimit (5 GB) erreicht. Bitte Dateien löschen.');
      e.target.value = ''; return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('project_id', '');
    fd.append('folder_id', selectedFolder || '');
    try {
      const res = await axios.post(`${API}/documents/upload`, fd);
      setDocs(prev => [...prev, res.data]);
      const sRes = await axios.get(`${API}/storage/stats`);
      setStorage(sRes.data);
      toast.success('Hochgeladen');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const handleDeleteDoc = async (id) => {
    await axios.delete(`${API}/documents/${id}`);
    setDocs(prev => prev.filter(d => d.id !== id));
    const sRes = await axios.get(`${API}/storage/stats`);
    setStorage(sRes.data);
    toast.success('Gelöscht');
  };

  const openFolderPerms = (folder) => { setPermsTarget(folder); setPermsType('folder'); setSelectedUserIds(folder.permissions || []); setShowPerms(true); };
  const openDocPerms = (doc) => { setPermsTarget(doc); setPermsType('document'); setSelectedUserIds(doc.permissions || []); setShowPerms(true); };

  const handleSavePerms = async () => {
    try {
      const url = permsType === 'folder' ? `${API}/folders/${permsTarget.id}/permissions` : `${API}/documents/${permsTarget.id}/permissions`;
      const res = await axios.put(url, { user_ids: selectedUserIds });
      if (permsType === 'folder') setFolders(prev => prev.map(f => f.id === permsTarget.id ? res.data : f));
      else setDocs(prev => prev.map(d => d.id === permsTarget.id ? res.data : d));
      setShowPerms(false);
      toast.success('Berechtigungen aktualisiert');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const toggleUserPerm = (userId) => setSelectedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);

  const currentDocs = search ? docs.filter(d => d.filename?.toLowerCase().includes(search.toLowerCase())) : (selectedFolder ? getFolderDocs(selectedFolder) : getUnsortedDocs());
  const currentFolderObj = folders.find(f => f.id === selectedFolder);
  const storagePercent = storage ? Math.min((storage.used_bytes / storage.max_bytes) * 100, 100) : 0;

  const FolderTree = ({ folder, depth = 0 }) => {
    const isExpanded = expandedFolders[folder.id];
    const isSelected = selectedFolder === folder.id;
    const children = getChildren(folder.id);
    const docCount = getFolderDocs(folder.id).length;
    const hasPerms = folder.permissions?.length > 0;

    return (
      <div>
        <div className={`flex items-center gap-1 py-1.5 px-2 rounded-sm cursor-pointer transition-colors group
          ${isSelected ? 'bg-amber-500/10 text-amber-400' : 'text-slate-400 hover:text-white hover:bg-slate-800/50'}`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }} data-testid={`folder-${folder.id}`}>
          {children.length > 0 ? (
            <button onClick={(e) => { e.stopPropagation(); toggleExpand(folder.id); }} className="shrink-0">
              {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          ) : <span className="w-3.5" />}
          <div className="flex items-center gap-2 flex-1 min-w-0" onClick={() => { setSelectedFolder(folder.id); setSearch(''); }}>
            {isSelected ? <FolderOpen className="w-4 h-4 text-amber-400 shrink-0" strokeWidth={1.5} /> : <Folder className="w-4 h-4 shrink-0" strokeWidth={1.5} />}
            <span className="text-sm truncate">{folder.name}</span>
            {docCount > 0 && <span className="text-[10px] font-mono text-slate-600">{docCount}</span>}
            {hasPerms && <Shield className="w-3 h-3 text-blue-400 shrink-0" />}
          </div>
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-white shrink-0" onClick={e => e.stopPropagation()}>
                  <MoreVertical className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-slate-900 border-slate-800 text-slate-200">
                <DropdownMenuItem onClick={() => { setNewFolderParent(folder.id); setShowNewFolder(true); }}
                  className="text-slate-200 focus:bg-slate-800"><FolderPlus className="w-4 h-4 mr-2" />Unterordner</DropdownMenuItem>
                <DropdownMenuItem onClick={() => openRename(folder)}
                  className="text-slate-200 focus:bg-slate-800"><Pencil className="w-4 h-4 mr-2" />Umbenennen</DropdownMenuItem>
                <DropdownMenuItem onClick={() => openFolderPerms(folder)}
                  className="text-slate-200 focus:bg-slate-800"><Shield className="w-4 h-4 mr-2" />Berechtigungen</DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-800" />
                <DropdownMenuItem onClick={() => handleDeleteFolder(folder.id)}
                  className="text-rose-400 focus:bg-rose-400/10"><Trash2 className="w-4 h-4 mr-2" />Ordner löschen</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {isExpanded && children.map(c => <FolderTree key={c.id} folder={c} depth={depth + 1} />)}
      </div>
    );
  };

  return (
    <div className="animate-fade-in" data-testid="dokumente-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-3xl font-bold text-white tracking-tight">Dokumente</h1>
        <div className="flex gap-2">
          {canManage && (
            <Button onClick={() => { setNewFolderParent(selectedFolder); setShowNewFolder(true); }} data-testid="create-folder-button"
              className="bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700 rounded-sm">
              <FolderPlus className="w-4 h-4 mr-2" />Neuer Ordner
            </Button>
          )}
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-global-doc"
            className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)]">
            <Upload className="w-4 h-4 mr-2" />{uploading ? 'Laden...' : 'Hochladen'}
          </Button>
          <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} accept="*/*" />
        </div>
      </div>

      {/* Storage bar */}
      {storage && (
        <div className="mb-4 bg-slate-900/50 border border-slate-800/60 rounded-sm p-3 flex items-center gap-4" data-testid="storage-bar">
          <HardDrive className="w-4 h-4 text-slate-400 shrink-0" strokeWidth={1.5} />
          <div className="flex-1">
            <Progress value={storagePercent} className="h-2 bg-slate-800" />
          </div>
          <span className="text-xs font-mono text-slate-400 shrink-0">
            {formatSize(storage.used_bytes)} / {formatSize(storage.max_bytes)}
            <span className="text-slate-600 ml-2">({storage.file_count} Dateien)</span>
          </span>
          {storagePercent > 80 && <Badge className="text-rose-400 bg-rose-400/10 border-rose-400/20 rounded-sm text-[10px]">Fast voll</Badge>}
        </div>
      )}

      <div className="flex gap-4" style={{ minHeight: '480px' }}>
        {/* Folder sidebar */}
        <div className="w-60 shrink-0 bg-slate-900/50 border border-slate-800/60 rounded-sm overflow-hidden flex flex-col">
          <div className="p-3 border-b border-slate-800/40">
            <p className="text-xs font-mono uppercase text-slate-500 tracking-wider">Ordner</p>
          </div>
          <div className="flex-1 overflow-y-auto p-1">
            <div className={`flex items-center gap-2 py-1.5 px-3 rounded-sm cursor-pointer transition-colors
              ${selectedFolder === null && !search ? 'bg-amber-500/10 text-amber-400' : 'text-slate-400 hover:text-white hover:bg-slate-800/50'}`}
              onClick={() => { setSelectedFolder(null); setSearch(''); }} data-testid="folder-unsorted">
              <File className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              <span className="text-sm">Unsortiert</span>
              <span className="text-[10px] font-mono text-slate-600">{getUnsortedDocs().length}</span>
            </div>
            {rootFolders.map(f => <FolderTree key={f.id} folder={f} />)}
          </div>
          <div className="p-2 border-t border-slate-800/40 text-[10px] text-slate-600 font-mono text-center">
            {folders.length} Ordner · Max 300 MB/Datei
          </div>
        </div>

        {/* Document list */}
        <div className="flex-1 flex flex-col">
          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input data-testid="doc-search" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Alle Dokumente durchsuchen..." className="pl-10 bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" />
            </div>
            {currentFolderObj && !search && (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Folder className="w-4 h-4" />
                <span>{currentFolderObj.name}</span>
                {currentFolderObj.permissions?.length > 0 && (
                  <Badge className="text-[10px] bg-blue-400/10 text-blue-400 border-blue-400/20 rounded-sm">
                    <Shield className="w-3 h-3 mr-1" />{currentFolderObj.permissions.length}
                  </Badge>
                )}
              </div>
            )}
          </div>

          <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm flex-1 overflow-auto">
            {currentDocs.length ? currentDocs.map(d => {
              const hasDocPerms = d.permissions?.length > 0;
              const Icon = getFileIcon(d.content_type);
              return (
                <div key={d.id} className="flex items-center justify-between py-3 px-4 border-b border-slate-800/40 hover:bg-slate-900/80 transition-colors group"
                  data-testid={`doc-row-${d.id}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Icon className="w-5 h-5 text-slate-400 shrink-0" strokeWidth={1.5} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-300 truncate">{d.filename}</p>
                      <p className="text-xs text-slate-500">
                        {formatSize(d.size)} · {d.uploaded_by_name}
                        {search && d.folder_id && <span className="ml-1 text-slate-600">· {folders.find(f => f.id === d.folder_id)?.name}</span>}
                      </p>
                    </div>
                    {hasDocPerms && (
                      <Badge className="text-[10px] bg-blue-400/10 text-blue-400 border-blue-400/20 rounded-sm shrink-0">
                        <Shield className="w-3 h-3 mr-1" />{d.permissions.length}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {canManage && (
                      <Button variant="ghost" size="icon" onClick={() => openDocPerms(d)}
                        className="text-slate-400 hover:text-blue-400 opacity-0 group-hover:opacity-100"><Shield className="w-4 h-4" /></Button>
                    )}
                    <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer">
                      <Button variant="ghost" size="icon" className="text-slate-400 hover:text-amber-400"><Download className="w-4 h-4" /></Button>
                    </a>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(d.id)} className="text-slate-400 hover:text-rose-400"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              );
            }) : (
              <div className="p-8 text-center text-slate-500">
                {search ? 'Keine Dokumente gefunden' : selectedFolder ? 'Ordner ist leer' : 'Keine unsortierten Dokumente'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Folder */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader>
            <DialogTitle className="text-white">
              {newFolderParent ? `Unterordner in "${folders.find(f => f.id === newFolderParent)?.name}"` : 'Neuer Ordner'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Ordnername</Label>
              <Input data-testid="folder-name-input" value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                placeholder="z.B. Baupläne, Verträge..." className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1"
                onKeyDown={e => e.key === 'Enter' && handleCreateFolder()} autoFocus />
            </div>
            <Button onClick={handleCreateFolder} data-testid="create-folder-submit"
              className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">Ordner erstellen</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename Folder */}
      <Dialog open={showRename} onOpenChange={v => { setShowRename(v); if (!v) setRenameTarget(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader><DialogTitle className="text-white">Ordner umbenennen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Neuer Name</Label>
              <Input data-testid="rename-folder-input" value={renameName} onChange={e => setRenameName(e.target.value)}
                className="bg-slate-950 border-slate-800 text-slate-200 mt-1"
                onKeyDown={e => e.key === 'Enter' && handleRename()} autoFocus />
            </div>
            <Button onClick={handleRename} data-testid="rename-folder-submit"
              className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">Umbenennen</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Permissions */}
      <Dialog open={showPerms} onOpenChange={setShowPerms}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-400" />
              Berechtigungen — {permsTarget?.name || permsTarget?.filename}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              {selectedUserIds.length === 0
                ? 'Keine Einschränkung — alle haben Zugriff.'
                : `${selectedUserIds.length} Mitarbeiter freigegeben.`}
            </p>
            <div className="bg-slate-950/50 border border-slate-800/40 rounded-sm max-h-[300px] overflow-y-auto">
              {users.map(u => (
                <label key={u.id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-800/30 hover:bg-slate-800/30 cursor-pointer">
                  <Checkbox checked={selectedUserIds.includes(u.id)} onCheckedChange={() => toggleUserPerm(u.id)}
                    className="border-slate-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-200">{u.name}</p>
                    <p className="text-xs text-slate-500">{u.email}</p>
                  </div>
                  <Badge className={`text-[10px] rounded-sm ${u.role === 'admin' ? 'text-rose-400 bg-rose-400/10' : u.role === 'projektleiter' ? 'text-blue-400 bg-blue-400/10' : 'text-emerald-400 bg-emerald-400/10'}`}>
                    {u.role}
                  </Badge>
                </label>
              ))}
            </div>
            {selectedUserIds.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setSelectedUserIds([])} className="text-slate-400 hover:text-white text-xs">
                <X className="w-3 h-3 mr-1" />Alle Freigaben entziehen
              </Button>
            )}
            <Button onClick={handleSavePerms} data-testid="save-permissions"
              className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">Berechtigungen speichern</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
